import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useTheme } from 'styled-components';
import { isEqual } from 'lodash';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  ButtonMode,
  EditIcon,
  Notification,
  NotificationType
} from 'edenred-ui';

import {
  BENEFIT_TO_MY_CHOICE_KEY,
  ContractedBenefitsSettings,
  IBenefits,
  MyChoiceSettings
} from '@epi-models/pages/BenefitGroups';
import { mapContractedBenefitsToMyChoiceSettings } from '@epi-helpers/BenefitGroupHelper';
import { BenefitCheckbox } from '@epi-shared/components/BenefitGroups/BenefitCheckbox/BenefitCheckbox';
import { useUpdateMyChoiceSettingsMutation } from '@epi-api/benefitGroups/benefitGroupsApi';
import { showNotification } from '@epi-actions/overlays';
import { BenefitType } from '@epi-constants/benefitTypes';
import { useOnboardingWizard } from '@epi-shared/hooks';

export const MyChoiceTile = ({
  contractedBenefits
}: {
  contractedBenefits: ContractedBenefitsSettings[];
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { fontSemiBold, lightBlue5, gray50, gray5 } = useTheme();
  const [editMode, setEditMode] = useState(false);
  const settingsFromBenefits = useMemo(
    () => mapContractedBenefitsToMyChoiceSettings(contractedBenefits),
    [contractedBenefits]
  );
  const [savedSettings, setSavedSettings] =
    useState<MyChoiceSettings>(settingsFromBenefits);
  const [draftSettings, setDraftSettings] =
    useState<MyChoiceSettings>(settingsFromBenefits);
  const [updateMyChoiceSettings, { isLoading }] =
    useUpdateMyChoiceSettingsMutation();
  const { isOnBoardingActive } = useOnboardingWizard();
  const isMyChoiceValid =
    contractedBenefits.filter(({ myChoiceEnabled }) => myChoiceEnabled).length >
    1;

  useEffect(() => {
    setSavedSettings(settingsFromBenefits);
    if (!editMode) {
      setDraftSettings(settingsFromBenefits);
    }
  }, [settingsFromBenefits, editMode]);

  const handleToggle = (benefitType: IBenefits) => {
    const key = BENEFIT_TO_MY_CHOICE_KEY[benefitType];
    setDraftSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCancel = () => {
    setDraftSettings(savedSettings);
    setEditMode(false);
  };

  const handleSave = async () => {
    try {
      await updateMyChoiceSettings({
        payload: { myChoiceSettings: draftSettings }
      }).unwrap();
      setSavedSettings(draftSettings);
      setEditMode(false);
      dispatch(
        showNotification(
          { message: 'containers.benefit_groups.my_choice.success_message' },
          'success'
        )
      );
    } catch {
      dispatch(showNotification({ message: 'messages.error' }, 'error'));
    }
  };

  const displaySettings = editMode ? draftSettings : savedSettings;
  const hasChanges = !isEqual(draftSettings, savedSettings);

  return (
    <Box pt={3} bgcolor={editMode ? lightBlue5 : gray5} my={5}>
      <Box display="flex" justifyContent="space-between" px={3}>
        <Box>
          <Box fontSize={24} fontWeight={fontSemiBold} mb={1}>
            {t('containers.benefit_groups.my_choice.title')}
          </Box>
          <Box fontSize={16} color={gray50} mb={3}>
            <Trans i18nKey="containers.benefit_groups.my_choice.subtitle" />
          </Box>
        </Box>
        {!editMode && (
          <Box width={130}>
            <Button
              id="my-choice-edit-btn"
              onClick={() => setEditMode(true)}
              mode={ButtonMode.Link}
              minWidth={135}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <EditIcon />
                {t('containers.benefit_groups.my_choice.buttons.edit')}
              </Box>
            </Button>
          </Box>
        )}
      </Box>
      <Box display="flex" gap={3} px={3} pb={3}>
        {contractedBenefits.map(
          (benefit: ContractedBenefitsSettings) =>
            benefit.benefitType !== BenefitType.Lunch && (
              <BenefitCheckbox
                key={benefit.benefitType}
                benefitType={benefit.benefitType}
                isChecked={
                  displaySettings[BENEFIT_TO_MY_CHOICE_KEY[benefit.benefitType]]
                }
                handleToggleCheckbox={() => handleToggle(benefit.benefitType)}
                disabled={!editMode}
              />
            )
        )}
      </Box>
      {isOnBoardingActive && !isMyChoiceValid && (
        <Box px={3} pb={3}>
          <Notification
            id="my-choice-info"
            type={NotificationType.Info}
            text={<Box>{t('containers.benefit_groups.my_choice.info')}</Box>}
          />
        </Box>
      )}
      {editMode && (
        <Box boxShadow="0px -4px 10px -4px rgba(0, 0, 0, 0.1)" px={3} py={2}>
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button
              id="my-choice-cancel-btn"
              onClick={handleCancel}
              mode={ButtonMode.Link}
              minWidth={135}
              disabled={isLoading}
            >
              {t('containers.benefit_groups.my_choice.buttons.cancel')}
            </Button>
            <Button
              id="my-choice-save-btn"
              onClick={handleSave}
              minWidth={135}
              disabled={isLoading || !hasChanges}
            >
              {t('containers.benefit_groups.my_choice.buttons.save')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};
